import { useState } from 'react';

/**
 * The submit bar at the foot of the wizard.
 *
 * Enabled only when the client engine reports zero required-visible fields outstanding — but that is
 * a HINT, not the authority: the button triggers a save-then-submit, and the server re-validates
 * against the persisted answers. So the flow is honest even if the optimistic client count and the
 * server's differ (e.g. a save that hasn't landed): the server 409s and the wizard re-syncs.
 *
 * When fields remain, the bar says how many, rather than a dead button with no explanation — a
 * client should never wonder why they can't finish.
 */
export function CompleteBar({ outstanding, onComplete }: { outstanding: number; onComplete: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canComplete = outstanding === 0;

  const submit = async () => {
    if (busy || !canComplete) return;
    setBusy(true);
    setError(null);
    try {
      await onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'לא הצלחנו לשלוח. נסו שוב.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="room-completebar">
      {!canComplete && (
        <p className="room-completebar-hint" role="status" aria-live="polite">
          נותרו {outstanding} שדות חובה למילוי
        </p>
      )}
      {error && (
        <p className="room-error" role="alert">
          {error}
        </p>
      )}
      <button type="button" className="btn-primary" disabled={!canComplete || busy} onClick={submit}>
        {busy ? 'שולח…' : 'סיום ושליחה'}
      </button>
    </div>
  );
}
